import type { OpsSettings } from '../../api/types.ts';

/**
 * `setting.ops` is a free-form JSON column (SPEC AC-OPS-10) that starts life as `null` on any
 * install seeded before this field existed, and Strapi's partial-update semantics mean a client
 * that only ever wrote `ops.marquee` would leave `ops.attract` absent forever. Every reader in
 * ops-dualscreen goes through this merge so a missing/partial tree renders the documented
 * defaults instead of crashing — the ONE place that risk is handled.
 */
export const DEFAULT_OPS_SETTINGS: OpsSettings = {
  marquee: {
    enabled: true,
    // MVP-389: Thai, because this is what a venue's projector shows until somebody edits it, and
    // the audience reading it is Thai (see the language default this app ships with). It is seed
    // data, not rendered copy — no `t()` can reach it, since it arrives from `setting.ops` at
    // runtime — so the default value itself is the only place to fix it.
    text: 'ยินดีต้อนรับ — ขอเพลงจากมือถือได้เลย',
    speedPxPerSec: 60,
    position: 'bottom',
  },
  attract: {
    mode: 'slideshow',
    intervalSec: 8,
    showJoinQr: true,
    showRotation: true,
    message: '',
  },
  overlays: {
    nowPlaying: true,
    upNext: true,
    countdown: true,
    missingLyrics: false,
    durationSec: 8,
  },
  hotkeys: {},
  // MVP-667. Off, matching `autoDuckPrefs`'s own default and for the same reason: the feature asks
  // for a microphone permission and changes the mix by itself, and neither is something to start
  // doing to an existing venue on upgrade without being asked. `normal` is 6 dB — the margin every
  // measurement in MVP-663/665 was taken against.
  // MVP-683: `lift: 'normal'` is +3 dB, the fixed value MVP-674 shipped, so a venue that never
  // opens this setting behaves exactly as it did before the setting existed.
  autoDuck: { enabledByDefault: false, sensitivity: 'normal', depth: 'normal', lift: 'normal' },
};

export function mergeOpsSettings(stored: Partial<OpsSettings> | null | undefined): OpsSettings {
  return {
    marquee: { ...DEFAULT_OPS_SETTINGS.marquee, ...stored?.marquee },
    attract: { ...DEFAULT_OPS_SETTINGS.attract, ...stored?.attract },
    overlays: { ...DEFAULT_OPS_SETTINGS.overlays, ...stored?.overlays },
    hotkeys: { ...DEFAULT_OPS_SETTINGS.hotkeys, ...stored?.hotkeys },
    autoDuck: { ...DEFAULT_OPS_SETTINGS.autoDuck, ...stored?.autoDuck },
  };
}
